import { PublicKey, TransactionInstruction } from '@solana/web3.js';
import { TOKEN_2022_PROGRAM_ID, ACCOUNT_SIZE } from '@solana/spl-token';

// Token-2022 stores a 1-byte AccountType marker right after ACCOUNT_SIZE in the mint buffer.
const ACCOUNT_TYPE_SIZE = 1;

// Token-2022 wire-format constants for the Pausable extension.
// Verified against solana-program/token-2022 interface/src/extension/pausable.
// Pausable was added in @solana/spl-token >= 0.4.6; this file lets us use it
// from 0.3.x without bumping the SDK.
export const PAUSABLE_OUTER_DISCRIMINATOR = 44;
const PAUSABLE_INIT = 0;
const PAUSABLE_PAUSE = 1;
const PAUSABLE_RESUME = 2;

export const PAUSABLE_EXTENSION_TYPE = 26;

// 4-byte TLV header (type u16 + length u16) + 33-byte PausableConfig
// (32-byte authority + 1-byte paused).
const PAUSABLE_TLV_DATA_LEN = 33;
export const PAUSABLE_EXTENSION_MINT_SPACE = 4 + PAUSABLE_TLV_DATA_LEN;

const TLV_HEADER_LEN = 4;

const ZERO_PUBKEY = new PublicKey(new Uint8Array(32));

export function createInitializePausableConfigInstruction(
  mint: PublicKey,
  authority: PublicKey | null,
  programId: PublicKey = TOKEN_2022_PROGRAM_ID,
): TransactionInstruction {
  const data = Buffer.alloc(2 + 32);
  data.writeUInt8(PAUSABLE_OUTER_DISCRIMINATOR, 0);
  data.writeUInt8(PAUSABLE_INIT, 1);
  const authBytes = (authority ?? ZERO_PUBKEY).toBuffer();
  authBytes.copy(data, 2);
  return new TransactionInstruction({
    programId,
    keys: [{ pubkey: mint, isSigner: false, isWritable: true }],
    data,
  });
}

function createPauseOrResumeInstruction(
  inner: number,
  mint: PublicKey,
  authority: PublicKey,
  multiSigners: PublicKey[],
  programId: PublicKey,
): TransactionInstruction {
  const data = Buffer.alloc(2);
  data.writeUInt8(PAUSABLE_OUTER_DISCRIMINATOR, 0);
  data.writeUInt8(inner, 1);
  const isSingleSigner = multiSigners.length === 0;
  const keys = [
    { pubkey: mint, isSigner: false, isWritable: true },
    { pubkey: authority, isSigner: isSingleSigner, isWritable: false },
    ...multiSigners.map((s) => ({ pubkey: s, isSigner: true, isWritable: false })),
  ];
  return new TransactionInstruction({ programId, keys, data });
}

export function createPauseInstruction(
  mint: PublicKey,
  authority: PublicKey,
  multiSigners: PublicKey[] = [],
  programId: PublicKey = TOKEN_2022_PROGRAM_ID,
): TransactionInstruction {
  return createPauseOrResumeInstruction(PAUSABLE_PAUSE, mint, authority, multiSigners, programId);
}

export function createResumeInstruction(
  mint: PublicKey,
  authority: PublicKey,
  multiSigners: PublicKey[] = [],
  programId: PublicKey = TOKEN_2022_PROGRAM_ID,
): TransactionInstruction {
  return createPauseOrResumeInstruction(PAUSABLE_RESUME, mint, authority, multiSigners, programId);
}

export interface PausableConfig {
  authority: PublicKey | null;
  paused: boolean;
}

// Token-2022 Mint TLV layout: [Mint base ... padded to ACCOUNT_SIZE=165]
// + [AccountType: 1 byte] + [TLV entries]. Each TLV entry is 2-byte type LE,
// 2-byte length LE, then `length` bytes of data.
export function parsePausableConfigFromMint(buffer: Buffer): PausableConfig | null {
  const tlvStart = ACCOUNT_SIZE + ACCOUNT_TYPE_SIZE;
  if (buffer.length <= tlvStart) {return null;}

  let cursor = tlvStart;
  while (cursor + TLV_HEADER_LEN <= buffer.length) {
    const type = buffer.readUInt16LE(cursor);
    const length = buffer.readUInt16LE(cursor + 2);
    const dataStart = cursor + TLV_HEADER_LEN;
    const dataEnd = dataStart + length;
    if (dataEnd > buffer.length) {return null;}

    if (type === PAUSABLE_EXTENSION_TYPE) {
      if (length < PAUSABLE_TLV_DATA_LEN) {return null;}
      const authBytes = buffer.slice(dataStart, dataStart + 32);
      const isZero = authBytes.every((b) => b === 0);
      const authority = isZero ? null : new PublicKey(authBytes);
      const paused = buffer.readUInt8(dataStart + 32) !== 0;
      return { authority, paused };
    }

    if (type === 0 && length === 0) {
      cursor += TLV_HEADER_LEN;
      continue;
    }
    cursor = dataEnd;
  }

  return null;
}
